import { Directive, EventEmitter, HostBinding, HostListener, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, debounceTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { isTruthy } from 'shared/util-common';

import { FlatTreeNode, TreeNode } from '@treetop/shared/model';

import { TreeDragDropInstruction, TreeDragDropService } from './tree-drag-drop.service';
import { TreeComponentService } from '../tree.component.service';

@Directive({
  selector: '[ttTreeDragDrop]',
  standalone: true,
})
export class TreeDragDropDirective<T extends TreeNode<unknown>> {
  private treeDragService = inject(TreeDragDropService);
  private treeService = inject(TreeComponentService);

  @Input('ttTreeDragDrop') public node: FlatTreeNode<T>;
  @Input() public ttTreeId: number;
  @Input() public readonly: boolean;
  @Input() public dragHoverExpandDelay = 1000;

  @Output() public treeNodeDropped = new EventEmitter<{
    instruction: TreeDragDropInstruction;
    node: FlatTreeNode<T>;
    target: FlatTreeNode<T>;
  }>();
  @Output() public elementDropped = new EventEmitter<string>();

  /**
   * The delay in milliseconds before expanding a node when dragging over it.
   */

  private get treeDragDropId(): string {
    return `tt-tree-drag-drop-${this.ttTreeId}`;
  }

  private dragOverDelay$ = new BehaviorSubject(false);

  @HostBinding('attr.draggable') public get draggable() {
    return !!this.node;
  }

  @HostListener('dragstart', ['$event'])
  public onDragStart(event: DragEvent): void {
    event.dataTransfer?.setData(this.treeDragDropId, 'tt-tree');

    const blockedNodeIds = this.getBlockedNodeIds(this.node.node);
    this.treeDragService.onDragStart(this.node, blockedNodeIds);
  }

  @HostListener('dragover', ['$event'])
  public onDragOver(event: DragEvent): void {
    const dragOrigin = this.getDragOrigin(event);
    if (!dragOrigin) {
      return;
    }

    this.treeDragService.onDragOver(event, this.node, dragOrigin);

    if (this.treeDragService.dragNode()?.node.id !== this.treeDragService.targetNode()?.node.id) {
      this.dragOverDelay$.next(true);
    }

    event.preventDefault();
  }

  @HostListener('dragenter', ['$event']) public onDragEnter(event: DragEvent): void {
    const dragOrigin = this.getDragOrigin(event);
    if (!dragOrigin) {
      return;
    }

    this.treeDragService.onDragEnter();
  }

  @HostListener('dragleave', ['$event']) public onDragLeave(event: DragEvent): void {
    const dragOrigin = this.getDragOrigin(event);
    if (!dragOrigin) {
      return;
    }

    const isLeaving = this.treeDragService.onDragLeave();

    this.dragOverDelay$.next(isLeaving);
  }

  @HostListener('drop', ['$event'])
  public onDrop(event: DragEvent): void {
    const dragOrigin = this.getDragOrigin(event);
    if (!dragOrigin) {
      return;
    }

    this.dragOverDelay$.next(false);

    if (dragOrigin === 'tt-tree-droppable') {
      this.elementDropped.emit(event.dataTransfer.getData('tt-tree-droppable'));
    } else {
      this.treeNodeDropped.emit({
        instruction: this.treeDragService.instruction(),
        target: this.node,
        node: this.treeDragService.dragNode(),
      });
    }

    this.treeDragService.onDragEnd();
  }

  @HostListener('dragend')
  public onDragEnd(): void {
    this.dragOverDelay$.next(false);

    this.treeDragService.onDragEnd();
  }

  constructor() {
    this.dragOverDelay$
      .pipe(takeUntilDestroyed(), distinctUntilChanged(), debounceTime(this.dragHoverExpandDelay), isTruthy())
      .subscribe(() => {
        if (this.treeDragService.dragNode()?.node.id !== this.node.node.id) {
          this.treeService.toggleExpandNode(this.node, 'open');
        }
      });
  }

  /**
   * Get the origin of the drag event. 'tt-tree' means the drag event is coming from the tree itself.
   * 'tt-tree-droppable' means the drag event is coming from a droppable element.
   * undefined means the drag event should be ignored.
   * @param event
   * @private
   */
  private getDragOrigin(event: DragEvent): 'tt-tree' | 'tt-tree-droppable' | undefined {
    if (!this.node) {
      return undefined;
    }

    if (event.dataTransfer?.types.includes(this.treeDragDropId)) {
      return 'tt-tree';
    }

    if (event.dataTransfer?.types.includes('tt-tree-droppable')) {
      return 'tt-tree-droppable';
    }

    return undefined;
  }

  private getBlockedNodeIds(node: TreeNode<unknown>): string[] {
    const blockedNodeIds: string[] = [];

    const collectBlockedNodes = (currentNode: TreeNode<unknown>) => {
      if (currentNode.children?.length) {
        currentNode.children.forEach((child) => {
          blockedNodeIds.push(child.id);
          collectBlockedNodes(child);
        });
      }
    };

    collectBlockedNodes(node);

    blockedNodeIds.push(node.id);
    blockedNodeIds.push(node.parentId);

    return blockedNodeIds;
  }
}
